import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, FormHelperText, Grid, FormControlLabel, Checkbox, TextField, MenuItem, Select, Typography, Paper, IconButton, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FMTextField } from '../Elements/input';
import useAuth from "../../context/AuthProvider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { apiClient } from '../../helper/api';
import Swal from "sweetalert2";
import * as Yup from 'yup';

const selectFieldSx = {
  width: '100%',
  '& .MuiSelect-select': { padding: '14.5px 16px' },
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};
const textFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
  '& .MuiInputBase-input': { padding: '5px 14px !important' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};

interface LicenseesContactInformation {
  partnerLincenseeInformationId: number;
  customerName: string;
  partnersId: number;
  postalAbbvs: string;
  licenseeName: string;
  pageSize : number;
  pageNumber : number;
  cancellationDate: string;
  stopStateReportsDate: string;
  entity: boolean;
  fein: string;
  licenseNumber: string;
  licenseState: string;
  licensee: string;
  mainLicensee: boolean;
  npn: string;
  title: string;
  status: string;
  statusReason: string;
  /** FL Agency License (FSLSO agency L-license). FL rows only. */
  agencyLicenseNumber: string;
  /** CA SLA Broker Number. CA rows only; client-level on PartnerAgencyDetails. */
  slaBrokerNumber: string;
}

const STATUS_OPTIONS = ['Active', 'Inactive'];
const STATUS_REASON_OPTIONS = ['Expired', 'Surrendered', 'Canceled', 'Other'];

/** FL Agency License: loads company-level value from PartnerAgencyDetails.
 * Super Admin may edit; save updates agency-wide. Other roles see the value read-only.
 */
const FlAgencyLicenseField: React.FC<{
  token?: string;
  partnersId: number;
  licenseState: string;
  value: string;
  canEdit: boolean;
  error?: string;
  onChange: (next: string) => void;
  textFieldSx: object;
}> = ({ token, partnersId, licenseState, value, canEdit, error, onChange, textFieldSx }) => {
  React.useEffect(() => {
    const isFlorida = String(licenseState || '').trim().toUpperCase() === 'FL';
    if (!isFlorida || !partnersId || partnersId === 0 || !token) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient(
          `/LicenseMatrix/getPartnerFlAgencyLicense?partnersId=${partnersId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (cancelled) return;
        const license = String(response?.data?.agencyLicenseNumber || '').trim();
        // Always surface the company value for all roles (edit gated by canEdit).
        onChange(license);
      } catch {
        // Keep whatever value is already in the form.
      }
    })();

    return () => {
      cancelled = true;
    };
    // value intentionally omitted — only refetch on partner/state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnersId, licenseState, token]);

  return (
    <FormControl error={!!error} sx={{ width: '100%' }}>
      <FormHelperText className="formtexthelper-heading">FL Agency License</FormHelperText>
      {/* Plain TextField so value is always shown; readOnly (not disabled) for non–Super Admin. */}
      <TextField
        name="agencyLicenseNumber"
        value={value ?? ''}
        onChange={(e) => {
          if (!canEdit) return;
          onChange(e.target.value);
        }}
        fullWidth
        variant="outlined"
        size="small"
        inputProps={{
          maxLength: 50,
          readOnly: !canEdit,
          'aria-readonly': !canEdit,
        }}
        sx={{
          ...textFieldSx,
          '& .MuiInputBase-input': {
            padding: '14.5px 14px !important',
            // Keep normal contrast when read-only so non-admins can still read the value.
            WebkitTextFillColor: 'inherit',
            color: 'inherit',
          },
          ...(canEdit
            ? {}
            : {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'action.hover',
                },
              }),
        }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

/** CA SLA Broker Number: loads company-level value from PartnerAgencyDetails.SLBrokerNumber.
 * Super Admin may edit; save updates client-wide. Other roles see the value read-only.
 */
const CaSlaBrokerNumberField: React.FC<{
  token?: string;
  partnersId: number;
  licenseState: string;
  value: string;
  canEdit: boolean;
  error?: string;
  onChange: (next: string) => void;
  textFieldSx: object;
}> = ({ token, partnersId, licenseState, value, canEdit, error, onChange, textFieldSx }) => {
  React.useEffect(() => {
    const isCalifornia = String(licenseState || '').trim().toUpperCase() === 'CA';
    if (!isCalifornia || !partnersId || partnersId === 0 || !token) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient(
          `/LicenseMatrix/getPartnerCaSlaBrokerNumber?partnersId=${partnersId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (cancelled) return;
        const number = String(response?.data?.slaBrokerNumber || '').trim();
        onChange(number);
      } catch {
        // Keep whatever value is already in the form.
      }
    })();

    return () => {
      cancelled = true;
    };
    // value intentionally omitted — only refetch on partner/state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnersId, licenseState, token]);

  return (
    <FormControl error={!!error} sx={{ width: '100%' }}>
      <FormHelperText className="formtexthelper-heading">CA SLA Broker Number</FormHelperText>
      <TextField
        name="slaBrokerNumber"
        value={value ?? ''}
        onChange={(e) => {
          if (!canEdit) return;
          onChange(e.target.value);
        }}
        fullWidth
        variant="outlined"
        size="small"
        inputProps={{
          maxLength: 50,
          readOnly: !canEdit,
          'aria-readonly': !canEdit,
        }}
        sx={{
          ...textFieldSx,
          '& .MuiInputBase-input': {
            padding: '14.5px 14px !important',
            WebkitTextFillColor: 'inherit',
            color: 'inherit',
          },
          ...(canEdit
            ? {}
            : {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'action.hover',
                },
              }),
        }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

interface Iprops {
  licenseesContactInformation: LicenseesContactInformation,
  handleClose: () => void,
  Open: boolean,
  handleSubmit: () => void
  initValues: LicenseesContactInformation
  fetchLicenseMatrixData: (pageNumber: number, pageSize: number) => Promise<any>;
  pageNumber: number;
  pageSize: number;
  States: { stateId: number, stateName: string, postalAbbv: string }[];
  Partners: { partnersId: number, customerName: string}[];
}

export const AddEditLicenseContact: React.FC<Iprops> = ({
  handleClose, Open, licenseesContactInformation, handleSubmit, initValues, fetchLicenseMatrixData, pageNumber, pageSize, States, Partners
}) => {
  const initialValues: LicenseesContactInformation = licenseesContactInformation ?  
        licenseesContactInformation : initValues;
  const { user } = useAuth();
  const token = user?.token;
  const isSuperAdmin = Boolean(user?.userRoles?.split(',').map((r) => r.trim()).includes('Super Admin'));
  const [SelectedPartner, setSelectedPartner] = React.useState<number>(initialValues.partnersId);

  // Guard against stale state: this dialog component stays mounted across
  // opens for different licensee rows (parent only toggles `Open`), so
  // SelectedPartner must be cleared every time it opens for a record —
  // otherwise a changed-then-cancelled Client selection from a previous
  // record can silently carry over and get submitted for a different one.
  React.useEffect(() => {
    if (Open) {
      setSelectedPartner(0);
    }
  }, [Open, licenseesContactInformation?.partnerLincenseeInformationId]);

  const requiredString = (message: string) => Yup.string().required(message);
    const validationSchema = Yup.object({
    npn: requiredString('NPN is required'),
    licenseState: requiredString('State is required'),
    partnersId: requiredString('Partner is required'),
    licensee: requiredString('Licensee is required'),
    licenseNumber: requiredString('License Number is required'),
    mainLicensee: requiredString('Main Licensee is required'),
    entity: requiredString('Entity is required'),
    status: Yup.string().oneOf(STATUS_OPTIONS).required('Status is required'),
    statusReason: Yup.string().when('status', {
      is: 'Inactive',
      then: (schema) => schema.oneOf([...STATUS_REASON_OPTIONS, '']).nullable(),
      otherwise: (schema) => schema
        .test('blank-when-active', 'Status Reason must be blank when Status is Active', (value) => !value || String(value).trim() === '')
        .nullable(),
    }),
    cancellationDate: Yup.string().when('status', {
      is: 'Inactive',
      then: (schema) => schema.required('Cancellation Date is required when Status is Inactive'),
      otherwise: (schema) => schema.nullable(),
    }),
    stopStateReportsDate: Yup.string().nullable(),
    agencyLicenseNumber: Yup.string().max(50, 'FL Agency License must not exceed 50 characters').nullable(),
    slaBrokerNumber: Yup.string().max(50, 'CA SLA Broker Number must not exceed 50 characters').nullable(),
  });

  async function saveLicensee(data: any) {
    const url = '/LicenseMatrix/AddLicensee';
    const isFlorida = String(data.licenseState || '').trim().toUpperCase() === 'FL';
    const isCalifornia = String(data.licenseState || '').trim().toUpperCase() === 'CA';
    const requestData = {
      Licensee: data.licensee,
      LicenseState: data.licenseState,
      LicenseNumber: data.licenseNumber,
      Title: data.title,
      PartnersId: SelectedPartner,
      MainLicensee: data.mainLicensee,
      Entity: data.entity,
      Npn: data.npn,
      Fein: data.fein,
      Status: data.status || 'Active',
      StatusReason: data.status === 'Active' ? null : (data.statusReason || null),
      CancellationDate: data.cancellationDate,
      ...(isSuperAdmin
        ? {
            StopStateReportsDate: data.stopStateReportsDate || null,
            AgencyLicenseNumber: isFlorida ? (data.agencyLicenseNumber || null) : null,
            SlaBrokerNumber: isCalifornia ? (data.slaBrokerNumber || null) : null,
          }
        : {}),
    };

    try {
      Swal.fire({
        title: 'Saving...',
        text: 'Please wait while we save your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      }).then(() => {
        fetchLicenseMatrixData(pageNumber,pageSize);
      });
      const response = await apiClient(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data added successfully.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  const updateLicenseesContactInformation = async (updatedData: any) => {
    try {
      Swal.fire({
        title: 'Updating...',
        text: 'Please wait while we update your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const payload = (Array.isArray(updatedData) ? updatedData : [updatedData]).map((row: any) => {
        const isFlorida = String(row.licenseState || '').trim().toUpperCase() === 'FL';
        const isCalifornia = String(row.licenseState || '').trim().toUpperCase() === 'CA';
        if (!isSuperAdmin) {
          const {
            agencyLicenseNumber,
            AgencyLicenseNumber,
            slaBrokerNumber,
            SlaBrokerNumber,
            stopStateReportsDate,
            StopStateReportsDate,
            ...rest
          } = row;
          return rest;
        }
        return {
          ...row,
          agencyLicenseNumber: isFlorida ? (row.agencyLicenseNumber || null) : null,
          slaBrokerNumber: isCalifornia ? (row.slaBrokerNumber || null) : null,
        };
      });

      const response = await apiClient(`/LicenseMatrix/UpdateLicensee`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data updated successfully.',
          confirmButtonText: 'OK'
        }).then(() => {
          fetchLicenseMatrixData(pageNumber,pageSize);
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={Open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: 900,
            minHeight: '50vh',
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          },
        }}
        sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BadgeOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
              Licensee Contact
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            aria-label="Close dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(data, { setSubmitting }) => {
            const payload = {
              ...data,
              partnersId: (SelectedPartner === 0 ? data.partnersId : SelectedPartner),
              Status: data.status || 'Active',
              StatusReason: data.status === 'Active' ? null : (data.statusReason || null),
              CancellationDate: data.cancellationDate,
              ...(isSuperAdmin ? { StopStateReportsDate: data.stopStateReportsDate || null } : {}),
            };
            if (data.partnerLincenseeInformationId === 0) {
              saveLicensee(payload);
            } else {
              updateLicenseesContactInformation([payload]);
              setSubmitting(false);
            }
            setSelectedPartner(0);
            handleSubmit();
          }}
        >
          {({ values, isSubmitting, errors, setFieldValue, setFieldError, validateField }) => (
            <Form className='overflow-auto'>
              <DialogContent sx={{ p: '0px' , '& > .MuiGrid-root': { px: { xs: 2, sm: 3 }, py: 3 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                      Licensee details
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licenseState} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">State</FormHelperText>
                            <Select
                              value={values.licenseState}
                              id="test-select-label"
                              label={null}
                              onChange={(e) => {
                                const nextState = e.target.value;
                                setFieldValue("licenseState", nextState);
                                if (String(nextState || '').trim().toUpperCase() !== 'FL') {
                                  initialValues.agencyLicenseNumber = '';
                                  setFieldValue("agencyLicenseNumber", '');
                                }
                                if (String(nextState || '').trim().toUpperCase() !== 'CA') {
                                  initialValues.slaBrokerNumber = '';
                                  setFieldValue("slaBrokerNumber", '');
                                }
                              }}
                              name="licenseState"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                              renderValue={(selected) => {
                                const selectedState = States.find((state) => state.postalAbbv === selected);
                                return selectedState ? selectedState.stateName : "Select State";
                              }}
                            >
                              <MenuItem key={0} value={0}>Select State</MenuItem>
                              {States.map((state, key) => (
                                <MenuItem key={key} value={state.postalAbbv}>{state.stateName}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.licenseState}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.partnersId} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Client</FormHelperText>
                            <Select
                              value={SelectedPartner === 0 ? values.partnersId : SelectedPartner}
                              id="test-select-label-client"
                              onChange={(e) => {
                                setFieldValue("customerName", Partners.find((item) => item.partnersId === e.target.value)?.customerName);
                                setSelectedPartner(Number(e.target.value));
                                // Partner change: clear until PAD fetch fills for FL / CA.
                                if (String(values.licenseState || '').trim().toUpperCase() === 'FL') {
                                  initialValues.agencyLicenseNumber = '';
                                  setFieldValue('agencyLicenseNumber', '');
                                }
                                if (String(values.licenseState || '').trim().toUpperCase() === 'CA') {
                                  initialValues.slaBrokerNumber = '';
                                  setFieldValue('slaBrokerNumber', '');
                                }
                              }}
                              name="partnersId"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                              renderValue={(selected) => {
                                const selectedPartner = Partners.find((partner) => partner.partnersId === selected);
                                return selectedPartner ? selectedPartner.customerName : "Select Client";
                              }}
                            >
                              <MenuItem key={0} value={0}>Select Client</MenuItem>
                              {Partners.map((partner, key) => (
                                <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.partnersId}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.status} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Status</FormHelperText>
                            <Select
                              value={values.status || 'Active'}
                              onChange={async (e) => {
                                const nextStatus = String(e.target.value);
                                await setFieldValue('status', nextStatus);
                                if (nextStatus === 'Active') {
                                  await setFieldValue('statusReason', '');
                                  setFieldError('cancellationDate', undefined);
                                }
                                await validateField('cancellationDate');
                                await validateField('statusReason');
                              }}
                              name="status"
                              IconComponent={ExpandMoreIcon}
                              sx={selectFieldSx}
                            >
                              {STATUS_OPTIONS.map((statusOption) => (
                                <MenuItem key={statusOption} value={statusOption}>{statusOption}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.status}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.statusReason} sx={{ width: '100%' }} disabled={(values.status || 'Active') === 'Active'}>
                            <FormHelperText className="formtexthelper-heading">Status Reason</FormHelperText>
                            <Select
                              value={values.statusReason || ''}
                              onChange={(e) => setFieldValue('statusReason', e.target.value)}
                              name="statusReason"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                              renderValue={(selected) => selected || 'Select Status Reason'}
                            >
                              <MenuItem value="">Select Status Reason</MenuItem>
                              {STATUS_REASON_OPTIONS.map((reason) => (
                                <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{errors.statusReason}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.cancellationDate} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">
                              Cancellation Date{(values.status || 'Active') === 'Inactive' ? ' *' : ''}
                            </FormHelperText>
                            <DatePicker
                              inputFormat="MM/DD/YYYY"
                              value={values.cancellationDate ? dayjs(values.cancellationDate) : null}
                              onChange={(v) => {
                                if (v && dayjs(v).isValid()) {
                                  const formattedDate = dayjs(v).format('YYYY-MM-DDTHH:mm:ss');
                                  initialValues.cancellationDate = formattedDate;
                                  setFieldValue("cancellationDate", formattedDate);
                                } else if (v === null) {
                                  initialValues.cancellationDate = '';
                                  setFieldValue("cancellationDate", '');
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  id="cancellationDate"
                                  name="cancellationDate"
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: false,
                                  }}
                                  sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                                />
                              )}
                            />
                            {errors.cancellationDate && <FormHelperText error>{errors.cancellationDate}</FormHelperText>}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Stop State Reports</FormHelperText>
                            <DatePicker
                              inputFormat="MM/DD/YYYY"
                              value={values.stopStateReportsDate ? dayjs(values.stopStateReportsDate) : null}
                              onChange={(v) => {
                                if (!isSuperAdmin) return;
                                if (v && dayjs(v).isValid()) {
                                  const formattedDate = dayjs(v).format('YYYY-MM-DDTHH:mm:ss');
                                  initialValues.stopStateReportsDate = formattedDate;
                                  setFieldValue('stopStateReportsDate', formattedDate);
                                } else if (v === null) {
                                  initialValues.stopStateReportsDate = '';
                                  setFieldValue('stopStateReportsDate', '');
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  id="stopStateReportsDate"
                                  name="stopStateReportsDate"
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: !isSuperAdmin,
                                  }}
                                  sx={{
                                    ...textFieldSx,
                                    '& .MuiInputBase-input': { padding: '14.5px 14px !important' },
                                    ...(!isSuperAdmin
                                      ? { '& .MuiOutlinedInput-root': { backgroundColor: 'action.hover' } }
                                      : {}),
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licensee} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Licensee</FormHelperText>
                            <FMTextField
                              label={null}
                              name="licensee"
                              value={initialValues.licensee}
                              onChange={(e: any) => {
                                initialValues.licensee = e.target.value;
                                setFieldValue("licensee", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.licenseNumber} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">License Number</FormHelperText>
                            <FMTextField
                              label={null}
                              name="licenseNumber"
                              value={initialValues.licenseNumber}
                              onChange={(e: any) => {
                                initialValues.licenseNumber = e.target.value;
                                setFieldValue("licenseNumber", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        {String(values.licenseState || '').trim().toUpperCase() === 'FL' && (
                          <Grid item xs={12} sm={6}>
                            <FlAgencyLicenseField
                              token={token}
                              partnersId={SelectedPartner === 0 ? values.partnersId : SelectedPartner}
                              licenseState={values.licenseState}
                              value={values.agencyLicenseNumber ?? ''}
                              canEdit={isSuperAdmin}
                              error={errors.agencyLicenseNumber as string | undefined}
                              onChange={(next) => {
                                initialValues.agencyLicenseNumber = next;
                                setFieldValue('agencyLicenseNumber', next);
                              }}
                              textFieldSx={textFieldSx}
                            />
                          </Grid>
                        )}

                        {String(values.licenseState || '').trim().toUpperCase() === 'CA' && (
                          <Grid item xs={12} sm={6}>
                            <CaSlaBrokerNumberField
                              token={token}
                              partnersId={SelectedPartner === 0 ? values.partnersId : SelectedPartner}
                              licenseState={values.licenseState}
                              value={values.slaBrokerNumber ?? ''}
                              canEdit={isSuperAdmin}
                              error={errors.slaBrokerNumber as string | undefined}
                              onChange={(next) => {
                                initialValues.slaBrokerNumber = next;
                                setFieldValue('slaBrokerNumber', next);
                              }}
                              textFieldSx={textFieldSx}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.npn} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">NPN</FormHelperText>
                            <FMTextField
                              label={null}
                              name="npn"
                              value={initialValues.npn}
                              onChange={(e: any) => {
                                initialValues.npn = e.target.value;
                                setFieldValue("npn", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!errors.fein} sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">FEIN</FormHelperText>
                            <FMTextField
                              label={null}
                              name="fein"
                              value={initialValues.fein}
                              onChange={(e: any) => {
                                initialValues.fein = e.target.value;
                                setFieldValue("fein", e.target.value);
                              }}
                              fullWidth
                              variant="outlined"
                              sx={{...textFieldSx, '& .MuiInputBase-input': { padding: '14.5px 14px !important' }}}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <FormControl sx={{ width: '100%' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={initialValues.mainLicensee}
                                      onChange={(e) => {
                                        initialValues.mainLicensee = e.target.checked;
                                        setFieldValue("mainLicensee", e.target.checked);
                                      }}
                                      name="mainLicense"
                                      color="primary"
                                    />
                                  }
                                  label="Main License"
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl sx={{ width: '100%' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={initialValues.entity}
                                      onChange={(e) => {
                                        initialValues.entity = e.target.checked;
                                        setFieldValue("entity", e.target.checked);
                                      }}
                                      name="entity"
                                      color="primary"
                                    />
                                  }
                                  label="Entity"
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  gap: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    setSelectedPartner(0);
                    handleClose();
                  }}
                  startIcon={<CloseOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#2099d2',
                      backgroundColor: '#2099d214',
                      color: '#2099d2',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={<SaveOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#2099d2',
                    '&:hover': { backgroundColor: '#176990' },
                    px: 2.5,
                  }}
                >
                  Submit
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </LocalizationProvider>
  );
}
